import { styled, Box } from "@mui/material";
import { ReactElement } from "react";
import React from "react";
import { FormattedMessage } from "react-intl";

import { parseTextAndUrlSegments, replaceURLs } from "@stw/extract-urls";

import { ThreadMessage } from "~frontend/__generated__/graphql";
import { InternalLink } from "~frontend/components/Link";
import MediaAttachment from "~frontend/components/MediaAttachment";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { userIsSubaccountAdminOrHigher } from "~frontend/util/roles";

import MessageInfoButton from "./MessageInfoButton";
import formatMessageTime from "./formatMessageTime";

const MessageWrapper = styled(Box)({
  maxWidth: "540px",
  marginBottom: "10px",
});

const MessageBubble = styled(Box)({
  color: palette.navy,
  borderRadius: "20px",
  borderBottomRightRadius: 0,
  padding: "24px",
  backgroundColor: palette.blueHighlight,

  "a, a:visited": {
    color: palette.navy,
  },
});

const MessageHeader = styled("div")({
  paddingLeft: "24px",
  marginBottom: "2px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontSize: 10,
  color: palette.darkGreen,
  "a, a:visited": {
    color: palette.darkGreen,
  },
});

export default function OutgoingMessage({
  message,
  onClickInfoButton,
  showCampaignContext,
}: {
  message: ThreadMessage;
  onClickInfoButton: () => void;
  showCampaignContext?: boolean;
}): ReactElement {
  const isStaff = useCurrentUserIsStaff();

  let messageText: React.ReactNode = message.text;
  if (message.shortenedLinks.length > 0) {
    const targetByShortenedLink: Record<string, string> = {};
    message.shortenedLinks.forEach(shortenedLink => {
      targetByShortenedLink[shortenedLink.shortUrl] = shortenedLink.targetUrl;
    });

    const messageTextSegments = parseTextAndUrlSegments(message.text);
    messageText = replaceURLs(messageTextSegments, url => {
      if (url in targetByShortenedLink) {
        return (
          <a
            href={targetByShortenedLink[url]}
            target="_blank"
            rel="noopener noreferrer"
          >
            {url}
          </a>
        );
      } else {
        return url;
      }
    });
  }

  return (
    <MessageWrapper>
      <MessageHeader>
        {showCampaignContext && <MessageLabel message={message} />}
      </MessageHeader>
      <MessageBubble>
        <Box>
          {message.mediaAttachments?.length
            ? message.mediaAttachments.map(attachment => (
                <React.Fragment key={attachment.url}>
                  <MediaAttachment
                    src={attachment.url}
                    contentType={attachment.mimeType}
                  />
                  <br />
                </React.Fragment>
              ))
            : null}
          <span
            style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }}
            data-testid="thread-message-history-label"
            data-message-direction="outbound"
          >
            {messageText}
          </span>
        </Box>
        <Box sx={{ fontSize: "10px" }}>
          {formatMessageTime(message.threadOrderTime)}
          {isStaff && showCampaignContext && (
            <MessageInfoButton onClick={onClickInfoButton} />
          )}
        </Box>
      </MessageBubble>
    </MessageWrapper>
  );
}

function MessageLabel({ message }: { message: ThreadMessage }): ReactElement {
  const { subaccountLink, membership } = useSubaccount()!;

  const isAdmin = userIsSubaccountAdminOrHigher(membership);

  if (message.isInitialOutboundForCampaign) {
    return (
      <>
        <span style={{ fontWeight: 500, textTransform: "uppercase" }}>
          <FormattedMessage
            defaultMessage="Initial message"
            description="Heading indicating that a message was the initial outbound for a campaign"
          />
        </span>
        :{" "}
        {isAdmin ? (
          <InternalLink
            to={subaccountLink(`/campaigns/${message.campaignShortId}`)}
            target="_blank"
          >
            {message.campaignName}
          </InternalLink>
        ) : (
          message.campaignName
        )}
      </>
    );
  } else if (message.createdByUserFullName && message.campaignName) {
    return (
      <Box>
        <FormattedMessage
          defaultMessage="Reply from {userFullName} in {campaignName}"
          description="Heading indicating that a message was a reply from a texter with campaign name"
          values={{
            userFullName: message.createdByUserFullName,
            campaignName: isAdmin ? (
              <InternalLink
                to={subaccountLink(`/campaigns/${message.campaignShortId}`)}
                target="_blank"
              >
                {message.campaignName}
              </InternalLink>
            ) : (
              message.campaignName
            ),
          }}
        />
      </Box>
    );
  } else if (message.createdByUserFullName) {
    return (
      <Box>
        <FormattedMessage
          defaultMessage="Reply from {userFullName}"
          description="Heading indicating that a message was a reply from a texter without a campaign name"
          values={{
            userFullName: message.createdByUserFullName,
          }}
        />
      </Box>
    );
  } else if (message.campaignName) {
    return (
      <Box>
        <FormattedMessage
          defaultMessage="Automated reply in {campaignName}"
          description="Heading indicating that a message was an automated reply a campaign name"
          values={{
            campaignName: isAdmin ? (
              <InternalLink
                to={subaccountLink(`/campaigns/${message.campaignShortId}`)}
                target="_blank"
              >
                {message.campaignName}
              </InternalLink>
            ) : (
              message.campaignName
            ),
          }}
        />
      </Box>
    );
  } else {
    return (
      <Box>
        <FormattedMessage
          defaultMessage="Automated message"
          description="Heading indicating that a message was an automated reply"
        />
      </Box>
    );
  }
}
